<template>
  <b-modal
    :id="id"
    hide-header
    hide-footer
    centered
    body-class="p-0"
    size="xl"
    no-close-on-backdrop
    @show="init"
    @hide="changeEditMode(false)"
  >
    <template #default="{ close }">
      <b-container class="acl-add-modal-container" fluid>
        <b-row class="acl-add-modal-header">
          <!-- title-text は flex アイテムのため、通常の半角スペースだと行頭/行末の
               折りたたみ対象になりブラウザに除去される。&nbsp; で明示的に空白を保持する -->
          <div class="acl-modal-header-title" v-if="targetType === 'TOPIC' && topicTitle">
            <span class="title-text">話題:&nbsp;</span>
            <span class="topic-title-ellipsis">{{ topicTitle }}</span>
            <span class="title-text">&nbsp;の閲覧権限設定</span>
          </div>
          <div class="acl-modal-header-title" v-else>{{ titleLabel }}</div>
          <b-button-close class="ml-auto p-3" @click="cancel(close)" />
        </b-row>
        <b-row class="acl-add-modal-body">
          <b-col class="acl-add-modal-left-panel" cols="4" no-gutters>
            <div v-if="departments.length || loading" class="acl-add-modal-body-header">
              <div class="body-header-title ml-3 pt-2">部署絞込</div>
            </div>
            <departments-tree
              v-if="departments.length || loading"
              class="department-tree"
              :departments="departments"
              :denyIdList="denyIdList"
              @selectDepartment="(value) => loadEvent(() => clickedDepartmentTree(value))"
            />
            <div v-else class="acl-add-modal-left-panel px-3">
              <div class="h-100 w-100" style="border: 1px solid #8ca6b7; overflow: scroll">
                <div class="p-2">
                  <h5><u>使い方</u></h5>
                  話題を閲覧できるユーザー単位で設定できます。<br />
                  話題の閲覧権限設定は所有者と管理者のみ設定可能です。<br /><br />

                  ①部署・ユーザーの絞込項目はユーザーを選択してください<br />
                  ➁ユーザーをチェックしてください。チェックされているユーザーは話題の閲覧が可能です。<br />
                  ③全解除ボタンですべてのユーザー閲覧不可となります。<br />
                  ④プルダウンから「すべてのユーザー」を選択すると、すべてのユーザーの閲覧設定の状況が表示されます。<br />
                  ⑤プルダウンから「閲覧制限部署またはユーザー」を選択すると、閲覧が制限されている部署やユーザーのみが表示されます。<br />
                  ⑥確認できたら、保存を押してください。<br />
                </div>
              </div>
            </div>
          </b-col>
          <b-col class="acl-add-modal-right-panel" cols="8">
            <div class="acl-add-modal-body-header">
              <div class="body-header-title">部署・ユーザの絞込</div>
              <div class="d-flex mt-auto">
                <dropdown
                  class="mr-2"
                  :activeIndex="modeIndex"
                  :options="searchOptions"
                  v-on:click="changeSearchMode"
                />
                <search-window v-on:update="updateSearchWindowInput" v-on:change="search" />
              </div>
            </div>
            <lower-list
              :class="`lower-list-component no-footer`"
              :lowerList="lowerList"
              :routePath="routePath"
              :denyIdList="denyIdList"
              :searching="searching"
              :selectDepartmentIdStr="selectDepartmentIdStr"
              :activeIndex="activeIndex"
              :modeOptions="modeOptions"
              :aclController="aclController"
              :searchMode="searchMode"
              @clickedEditButton="(value) => loadEvent(() => changeStatus(value))"
              @selectedAll="(value) => loadEvent(() => selectedAll(value))"
              @selectDepartment="(value) => loadEvent(() => selectDepartment(value))"
              @changeFilterMode="(value) => loadEvent(() => changeFilterMode(value))"
              @resetAll="() => loadEvent(() => resetAll())"
            />
            <!-- 表示方法について議論中(dxflow-1514)
                        <b-row class="acl-add-modal-body-footer" v-show="selectedList.length">
                            <b-row class="divider">
                                <div class="divider-body" />
                            </b-row>
                            <b-row class="badge-list" no-gutters>
                                <div class="badge-list-body">
                                    <b-badge
                                        v-for="item of selectedList"
                                        :key="item.id"
                                        :variant="item.isDeny ? 'danger' : 'info'"
                                        pill
                                        class="select-item-badge"
                                    >
                                        {{ item.name }}
                                        <b-button
                                            :class="`badge-clear-button ${item.isDeny ? 'deny' : 'allow'}`"
                                            :variant="`${item.isDeny ? 'danger' : 'info'}`"
                                            size="sm"
                                            @click="changeStatus(item)"
                                        >
                                            <b-icon-x />
                                        </b-button>
                                    </b-badge>
                                </div>
                            </b-row>
                        </b-row>
                        -->
          </b-col>
        </b-row>
        <b-row class="acl-add-modal-footer">
          <div class="d-flex flex-direction-row my-auto ml-auto mr-2">
            <custom-button
              class="mr-2"
              :label="'キャンセル'"
              variant="secondary"
              :size="'sm'"
              :rounded="true"
              v-on:confirm="cancel(close)"
            />
            <custom-button
              :label="'保存'"
              :size="'sm'"
              :rounded="true"
              v-on:confirm="save(close)"
            />
          </div>
        </b-row>
      </b-container>
      <b-overlay
        :show="loading"
        @hidden="loading = false"
        no-wrap
        opacity="0"
        spinner
        spinner-variant="primary"
      >
      </b-overlay>
    </template>
  </b-modal>
</template>

<style scoped lang="scss">
@import "../../../node_modules/bootstrap/scss/functions";
@import "../../../node_modules/bootstrap/scss/variables";

$modal-height: 80vh; // モーダル全体の高さ
$modal-header-height: 50px; // モーダルheaderの高さ
$modal-footer-height: 50px; // モーダルfooterの高さ

$modal-body-height: calc(
  #{$modal-height} - #{$modal-header-height} - #{$modal-footer-height}
); // モーダルbodyの高さ
// 左body
$left-body-header-height: 30px; // モーダルbody内のheaderの高さ
// 右body
$right-body-header-height: 67px; // モーダルbody内のheaderの高さ
$right-body-footer-height: 55px; // モーダルbody内のfooterの高さ

$allow: #00b7c6;
$deny: #d56c9c;

.acl-add-modal-container {
  height: $modal-height;

  .acl-add-modal-header {
    height: $modal-header-height;

    .acl-modal-header-title {
      display: flex;
      align-items: baseline;
      min-width: 0;
      padding: 16px;
      font-size: 20px;

      .title-text {
        flex-shrink: 0;
        white-space: nowrap;
      }

      .topic-title-ellipsis {
        flex: 1 1 auto;
        min-width: 0;
        overflow: hidden;
        white-space: nowrap;
        text-overflow: ellipsis;
      }
    }
  }

  .acl-add-modal-left-panel {
    height: $modal-body-height;
    padding-left: 0;
    padding-right: 0;

    .acl-add-modal-body-header {
      height: $left-body-header-height;
      padding: 8px 0;

      .body-header-title {
        display: flex;
        font-size: 16px;
      }
    }

    .department-tree {
      height: calc(#{$modal-body-height} - #{$left-body-header-height});
      max-height: calc(#{$modal-body-height} - #{$left-body-header-height});
      padding: 16px 16px 0 16px;
    }
  }

  .acl-add-modal-right-panel {
    height: $modal-body-height;

    .acl-add-modal-body-header {
      height: $right-body-header-height;
      padding: 8px 0;

      .body-header-title {
        display: flex;
        font-size: 16px;
      }
    }

    .lower-list-component {
      height: calc(
        #{$modal-body-height} - #{$right-body-footer-height} - #{$right-body-header-height}
      );

      &.no-footer {
        height: calc(#{$modal-body-height} - #{$right-body-header-height});
      }
    }

    .acl-add-modal-body-footer {
      height: $right-body-footer-height;
      display: flex;
      flex-direction: row;
      padding-top: 8px;
      padding-bottom: 8px;

      .divider {
        display: flex;
        margin: 0 15px;
        width: 100%;
        height: 0px;
        padding-bottom: 8px;

        .divider-body {
          border: 1px solid #8ca6b7;
          width: 100%;
        }
      }

      .badge-list {
        flex-direction: column;
        margin: 0 15px;
        max-width: calc(100% - 30px);
        overflow-x: auto;

        .badge-list-body {
          height: 30px;
          display: flex;
          margin: 4px 0;

          .select-item-badge {
            padding: 4px;
            margin-right: 4px;
            color: white;

            &.deny {
              background-color: $deny;
            }

            &.allow {
              background-color: $allow;
            }
          }

          .badge-clear-button {
            padding: 0;
            margin-bottom: 2px;
            color: white;
            outline-color: white;

            &.deny {
              background-color: $deny;
              border-color: $deny;
              &:focus {
                outline: none !important;
              }
            }

            &.allow {
              background-color: $allow;
              border-color: $allow;
              &:focus {
                outline: 0 !important;
              }
            }
          }
        }
      }
    }
  }

  .acl-add-modal-footer {
    height: $modal-footer-height;
  }
}
</style>

<script lang="ts">
import {
  computed,
  defineComponent,
  getCurrentInstance,
  nextTick,
  onMounted,
  ref,
  watch,
  type PropType,
} from "vue";
import type Vue from "vue";
import { useAppStore, useDomainStore, useTopicStore, useUserStore } from "@/store";
import DepartmentsTree from "./group-uesr-list/DepartmentsTree.vue";
import type { Department } from "./group-uesr-list/Departments/values/Department";
import LowerList from "./LowerList.vue";
import { LowerListItem, IModeOption } from "./lower-list-type";
import { useEditDiscard } from "@/composables/useEditDiscard";
import Acl from "../../model/acl";
import AclController from "./acl-controller";
import Dropdown from "../common/CustomDropdown.vue";
import SearchWindow from "../common/SearchWindow.vue";
import CustomButton from "../button/CustomButon.vue";
import { User } from "@/model";
import type { DirectDomainMembersType } from "@/direct-restapi-types";

import { SearchMode } from "./search-mode";
export { SearchMode };

export default defineComponent({
  name: "acl-edit-modal",
  components: {
    DepartmentsTree,
    LowerList,
    Dropdown,
    SearchWindow,
    CustomButton,
  },
  props: {
    id: { type: String, default: () => "", required: true },
    // storybook用
    departmentsSource: { type: Array as PropType<Department[]>, default: () => [] },
    // Storybook向け
    show: { type: Boolean, default: () => false },
    // Storybook向け
    setDomainId: { type: String, default: () => "" },
    domainUsers: {
      type: Object as PropType<DirectDomainMembersType>,
      default: () => {
        return { contents: [] };
      },
    },
    targetId: { type: String, default: () => "" },
    targetType: {
      type: String as PropType<"TOPIC" | "MESSAGE" | "COMMENT">,
      default: () => "TOPIC",
    },
    acl: { type: Object as PropType<Acl>, default: () => Acl.createDummy() },
  },
  setup(props) {
    // 旧 EditDiscardMixin → composable（1-D 利用側書き換え）
    const { changeEditMode, showEditDiscardAlert } = useEditDiscard();
    // $bvModal は BootstrapVue が生やす（Step 3 で composable 化予定）
    const vm = getCurrentInstance()?.proxy as Vue | undefined;

    const aclController = ref<AclController>(new AclController());
    const departments = ref<Department[]>([]);

    // denyIdMap から 拒否したユーザ/部署ID の配列を生成
    const denyIdList = ref<string[]>([]);

    // selectedIdMap から 選択したユーザ/部署ID の配列を生成
    const selectedList = ref<LowerListItem[]>([]);

    const lowerList = ref<LowerListItem[]>([]);
    const routePath = ref<Department[]>([]);
    const selectDepartmentIdStr = ref<string>("");
    const mode = ref<SearchMode>(SearchMode.ALL);
    const text = ref<string>("");

    const searchOptions = ref<SearchMode[]>([
      SearchMode.ALL,
      SearchMode.DEPARTMENT,
      SearchMode.USER,
    ]);

    const users = ref<DirectDomainMembersType>({ contents: [] });

    const loading = ref<boolean>(false);
    const searching = ref<boolean>(false);

    // リスト表示のモードのコントロール用
    const activeIndex = ref<number>(0);

    // 部署ツリーのローディング管理
    const treeLoading = ref<boolean>(false);

    // データ構築(init)の動作制御 trueでinit実行
    const startInit = ref<boolean>(true);

    watch(
      departments,
      (): void => {
        // 旧実装では Vuex ストア未注入（Storybook）時のみ departmentsSource を反映していた。
        // Pinia 移行後はストアが常に存在するため、実アプリ挙動（何もしない）を維持する。
      },
      { immediate: true }
    );

    watch(
      () => props.domainUsers,
      (value: DirectDomainMembersType): void => {
        if (value.contents.length > 0) {
          users.value = value;
          startInit.value = true;
        }
      },
      { immediate: true }
    );

    watch(
      () => props.targetId,
      async (): Promise<void> => {
        startInit.value = true;
        await init();
      }
    );

    watch(
      () => props.acl,
      async (): Promise<void> => {
        startInit.value = true;
        await init();
      }
    );

    // リストフィルターのモード一覧
    const modeOptions = computed((): IModeOption[] => {
      return departments.value.length
        ? [IModeOption.DEFAULT, IModeOption.USER, IModeOption.SET]
        : [IModeOption.USER, IModeOption.SET];
    });

    const modeIndex = computed((): number => {
      switch (mode.value) {
        case SearchMode.ALL: {
          return 0;
        }
        case SearchMode.DEPARTMENT: {
          return 1;
        }
        case SearchMode.USER: {
          return 2;
        }
        default: {
          return 0;
        }
      }
    });

    const searchMode = computed((): SearchMode | undefined => {
      if (!text.value) {
        return undefined;
      } else {
        return mode.value;
      }
    });

    const topicTitle = computed((): string | undefined => {
      if (props.targetType !== "TOPIC") return undefined;
      return useTopicStore().getOne(useAppStore().domainId, props.targetId)?.title;
    });

    const titleLabel = computed((): string => {
      switch (props.targetType) {
        case "TOPIC": {
          const topic = useTopicStore().getOne(useAppStore().domainId, props.targetId);
          if (topic) {
            return `話題: ${topic.title} の閲覧権限設定`;
          } else {
            return `新規話題の閲覧権限設定`;
          }
        }
        default: {
          return `閲覧権限設定`;
        }
      }
    });

    onMounted((): void => {
      if (props.show) {
        vm?.$bvModal.show(props.id);
      }
    });

    const init = async (): Promise<void> => {
      if (!startInit.value) return;
      if (!useAppStore().domainId) return;

      loading.value = true;

      aclController.value = new AclController();
      denyIdList.value = [];
      selectedList.value = [];
      lowerList.value = [];
      routePath.value = [];
      selectDepartmentIdStr.value = "";
      mode.value = SearchMode.ALL;
      text.value = "";
      activeIndex.value = 0;
      const domainId = useAppStore().domainId;
      const me = useUserStore().me as User;
      if (!me) return;
      aclController.value.editExceptionUser(me.id, {
        owner: false,
        operator: true,
        admin: false,
      });
      // 毎回最新の部署/組織メンバー情報を取得
      await useAppStore().fetchDomainInfo(domainId);
      users.value = useUserStore().getDomainMembers(domainId);
      departments.value = useDomainStore().getDepartments || [];
      if (departments.value.length > 0) {
        selectDepartmentIdStr.value = departments.value[0].departmentIdStr;
      }
      aclController.value.init(domainId, users.value, departments.value, props.acl);
      updateList();
      startInit.value = false;
      loading.value = false;
    };

    // 許可[+]/拒否[-]ボタンを押した時の処理
    const changeStatus = (item: LowerListItem): void => {
      /**
       * action: 今回の操作 (true: 許可する false: 拒否する)
       * status: 操作前の状態 (true: 許可状態 false: 拒否状態)
       * isDeny: 拒否リスト(denyIdList)に含まれるか (= !status)
       */
      const status = !aclController.value.isDeny(item.id);
      const action = !status;

      aclController.value.syncLowerList(item, action, true);
      updateList();
    };

    // 全てのチェック(チェックボックス)を押した時の処理
    const selectedAll = (checked: boolean): void => {
      const action = checked;
      // モードによって別処理
      switch (modeOptions.value[activeIndex.value]) {
        case IModeOption.DEFAULT: {
          // 親部署(現在洗濯中の部署)に対して 許可/拒否
          const item = aclController.value.convertLowerListItem(selectDepartmentIdStr.value);
          if (!item) return;
          aclController.value.syncLowerList(item, action, true);
          updateList();
          break;
        }
        case IModeOption.USER: {
          // 下階層のユーザ全てに対して 許可/拒否
          lowerList.value.map((item) => {
            aclController.value.syncLowerList(item, action, true);
          });
          updateList();
          break;
        }
        default: {
          break;
        }
      }
    };

    // 全ての設定を初期状態に戻す
    const resetAll = (): void => {
      aclController.value.getSetList().map((item) => {
        // *初期設定が全て許可のため 一括true
        aclController.value.syncLowerList(item, true, true);
      });
      updateList();
    };

    // ツリーから部署選択
    const clickedDepartmentTree = (department: Department): void => {
      // 部署遷移がある時、SET のみモード変更
      if (modeOptions.value[activeIndex.value] === IModeOption.SET) {
        activeIndex.value = 0;
      }

      const id = department.departmentIdStr;
      selectDepartment(id);
    };

    // 更新を表示へ反映させるための処理
    const updateList = (): void => {
      // 拒否リスト更新
      denyIdList.value = aclController.value.getDenyIdList();
      // 選択リスト更新
      selectedList.value.splice(0, selectedList.value.length);
      selectedList.value.push(...aclController.value.getSelectedList());
      // LowerList(現在表示中の階層)更新
      selectDepartment(selectDepartmentIdStr.value);
    };

    // 部署を選択した時の処理
    const selectDepartment = (id: string): void => {
      const department = departments.value.find((dep) => dep.departmentIdStr === id);

      selectDepartmentIdStr.value = id;

      lowerList.value.splice(0, lowerList.value.length);

      if (department) {
        routePath.value = getRoutePath(department);
      }

      // モードによって別処理
      switch (modeOptions.value[activeIndex.value]) {
        case IModeOption.DEFAULT: {
          // 1つ下階層の 部署 + ユーザ
          if (!department) return;
          lowerList.value.push(...aclController.value.getLowerDepartments(department));
          lowerList.value.push(...aclController.value.getLowerUsers(department));
          break;
        }
        case IModeOption.USER: {
          // 下階層のユーザ全て
          lowerList.value.push(
            ...aclController.value.getLowerAllUsers(selectDepartmentIdStr.value)
          );
          break;
        }
        case IModeOption.SET: {
          lowerList.value.push(...aclController.value.getSetList());
          break;
        }
        default: {
          break;
        }
      }
    };

    /**
     * 経路算出
     * (*速度重視のため、departmentsが正常な並びの時のみ正常に動作)
     */
    const getRoutePath = (department: Department): Department[] => {
      const index = departments.value.findIndex(
        (dep) => dep.departmentIdStr === department.departmentIdStr
      );
      const targets = departments.value.slice(0, index);
      const results: Department[] = [];
      let depth = department.depth;

      targets.reverse().forEach((target) => {
        if (target.depth === depth - 1) {
          results.unshift(target);
          depth--;
        }
        if (depth === 0) return;
      });
      results.push(department);
      return results;
    };

    const save = (close: () => CloseEvent): void => {
      const items = aclController.value.generateAcl();
      const acl = Acl.editAclItems(props.acl, items);
      useAppStore().setAclData({ id: props.targetId, acl: acl });
      close();
    };

    const cancel = (close: () => CloseEvent): void => {
      const closeEvents = () => {
        if (selectedList.value.length > 0) {
          aclController.value.reset(props.acl);
        }
        startInit.value = true;
        close();
      };
      changeEditMode(selectedList.value.length > 0);
      showEditDiscardAlert(closeEvents);
    };

    const search = (textValue: string): void => {
      if (!textValue) return;
      text.value = textValue;

      loading.value = true;
      searching.value = true;
      routePath.value.splice(0, routePath.value.length);
      lowerList.value.splice(0, lowerList.value.length);
      const promises: (() => LowerListItem[])[] = [];

      // 部署検索
      if (mode.value === SearchMode.ALL) {
        promises.push(() => aclController.value.search(text.value));
      } else if (mode.value === SearchMode.DEPARTMENT) {
        promises.push(() => aclController.value.search(text.value, "Department"));
      } else if (mode.value === SearchMode.USER) {
        promises.push(() => aclController.value.search(text.value, "User"));
      }

      setTimeout(
        () =>
          Promise.all(promises).then((results) => {
            results.map((result) => {
              lowerList.value.push(...result());
            });
            void nextTick(() => {
              loading.value = false;
              searching.value = false;
            });
          }),
        100
      );
    };

    const changeSearchMode = (index: number) => {
      const m = searchOptions.value[index];
      if (!m) return;
      mode.value = m;
      search(text.value);
    };

    // 表示されるリストの内容を変更
    const changeFilterMode = (param: { index: number; select: string | undefined }): void => {
      if (activeIndex.value === param.index) return;
      activeIndex.value = param.index;
      const id = param.select ? param.select : selectDepartmentIdStr.value;
      selectDepartment(id);
    };

    // 検索窓入力中の処理
    const updateSearchWindowInput = (updated: string): void => {
      text.value = updated;
      if (!text.value) {
        updateList();
      }
    };

    // ローディング用のイベント
    // setTimeoutでloadを挟むイベントと実行したい処理を並行で行う
    const loadEvent = (func: () => void): void => {
      if (loading.value) return;
      loading.value = true;
      setTimeout(() => {
        func();
        loading.value = false;
      }, 30);
    };

    return {
      aclController,
      departments,
      denyIdList,
      selectedList,
      lowerList,
      routePath,
      selectDepartmentIdStr,
      mode,
      text,
      searchOptions,
      users,
      loading,
      searching,
      activeIndex,
      treeLoading,
      startInit,
      modeOptions,
      modeIndex,
      searchMode,
      topicTitle,
      titleLabel,
      changeEditMode,
      init,
      changeStatus,
      selectedAll,
      resetAll,
      clickedDepartmentTree,
      updateList,
      selectDepartment,
      getRoutePath,
      save,
      cancel,
      search,
      changeSearchMode,
      changeFilterMode,
      updateSearchWindowInput,
      loadEvent,
    };
  },
});
</script>
